import type { JSX } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  addToast,
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useUpdateProduct } from '~/hooks/useProducts';

import { Input, Textarea } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal } from '~/components/Modal';

import type { Product } from '~/types';

/**
 * Zod schema for the product edit form.
 * Pattern: docs/design-patterns/frontend/form-handling.md
 */
const editProductSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Product name is required')
    .max(255, 'Product name must be less than 255 characters'),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional()
    .or(z.literal('')),
});

type EditProductFormData = z.infer<typeof editProductSchema>;

export interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
}

export function EditProductModal({ isOpen, onClose, product }: EditProductModalProps): JSX.Element {
  const updateProduct = useUpdateProduct();

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<EditProductFormData>({
    resolver: zodResolver(editProductSchema),
    mode: 'onBlur',
    defaultValues: { name: product.name, description: product.description ?? '' },
  });

  // Extract ref/onBlur from register (onChange is filtered by HeroUI, so we use onValueChange).
  const { onBlur: nameOnBlur, ref: nameRef } = register('name');
  const name = watch('name');

  // Re-sync the form to this product's current values whenever the modal opens — a reopen
  // (e.g. after the product was refetched, or for a different product) must never show stale
  // draft values from a previous open.
  useEffect(() => {
    if (isOpen) {
      reset({ name: product.name, description: product.description ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync on open, not every product identity change
  }, [isOpen]);

  const isSubmitting = updateProduct.isPending;

  const onSubmit = (data: EditProductFormData): void => {
    updateProduct.mutate(
      { id: product.id, data: { name: data.name, description: data.description || undefined } },
      {
        onError: (error: Error) => {
          addToast({ color: 'danger', description: error.message || 'Failed to update product' });
        },
        onSuccess: () => {
          addToast({ color: 'success', description: 'Product updated' });
          onClose();
        },
      },
    );
  };

  const handleClose = (): void => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const isNameValid = !!name?.trim() && !errors.name;

  return (
    <Modal isOpen={isOpen} maxWidth="lg" preventClosing={isSubmitting} onOpenChange={handleClose}>
      <ModalContent>
        <form aria-busy={isSubmitting} noValidate onSubmit={e => void handleSubmit(onSubmit)(e)}>
          <ModalHeader>Edit Product</ModalHeader>
          <Divider />
          <ModalBody>
            <Input
              ref={nameRef}
              description="Required"
              errorMessage={errors.name?.message}
              id="edit-product-name"
              isDisabled={isSubmitting}
              isInvalid={!!errors.name}
              isRequired
              label="Name"
              labelPlacement="outside-top"
              name="name"
              value={name}
              onBlur={e => void nameOnBlur(e)}
              onValueChange={value => {
                setValue('name', value, { shouldDirty: true });
              }}
            />

            <Textarea
              description="Optional"
              errorMessage={errors.description?.message}
              id="edit-product-description"
              isDisabled={isSubmitting}
              isInvalid={!!errors.description}
              label="Description"
              labelPlacement="outside-top"
              placeholder="Describe this product (optional)"
              {...register('description')}
            />
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button isDisabled={isSubmitting} type="button" variant="light" onPress={handleClose}>
              Cancel
            </Button>
            <Button
              aria-busy={isSubmitting}
              color="primary"
              data-testid="edit-product-submit"
              isDisabled={isSubmitting || !isNameValid}
              type="submit"
            >
              {isSubmitting ? <LoadingSpinner /> : 'Save'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
